import { Link } from 'expo-router';
import ArrowRight from 'lucide-react-native/icons/arrow-right';
import ChevronLeft from 'lucide-react-native/icons/chevron-left';
import ChevronRight from 'lucide-react-native/icons/chevron-right';
import { useRef } from 'react';
import { Pressable, StyleSheet, Text } from 'react-native';
import { useTranslation } from 'react-i18next';

import { ArticleCard } from '@/components/riviera/article-card';
import { HOME_ARTICLES_LIMIT } from '@/components/riviera/articles-section.types';
import { navHref } from '@/components/riviera/nav-links';
import { Fonts } from '@/constants/theme';
import { useArticles } from '@todo/core/hooks/use-articles';
import { useTheme } from '@/hooks/use-theme';
import { useLocale } from '@todo/core/i18n/use-locale';

import css from './carousel-section.module.css';

// Web-only mirror of articles-section.tsx that resolves its responsive layout
// with CSS at first paint instead of a post-mount `isLg` swap. See
// events-carousel.web.tsx / carousel-section.module.css for the rationale.
export function ArticlesSection() {
  const theme = useTheme();
  const { t } = useTranslation();
  const { locale } = useLocale();
  const scrollRef = useRef<HTMLDivElement>(null);
  const { articles, loading, error } = useArticles(locale);

  if (!loading && !error && articles.length === 0) return null;

  const scrollBy = (dir: number) => {
    scrollRef.current?.scrollBy({ left: dir * 280, behavior: 'smooth' });
  };

  return (
    <div className={css.section}>
      <div className={css.heading}>
        <h2 className={css.headingText} style={{ color: theme.text, fontFamily: Fonts?.serif }}>
          {t('articles.latest')}
        </h2>
        {/* Plain-object style — see the same Link in events-carousel.web.tsx. */}
        <Link href={navHref('/[lang]/articles', locale)} asChild>
          <Pressable accessibilityRole="link" style={styles.seeAll}>
            {({ pressed }) => (
              <>
                <Text
                  style={[
                    styles.seeAllText,
                    { color: theme.primary },
                    pressed && { opacity: 0.6 },
                  ]}>
                  {t('common.seeAll')}
                </Text>
                <ArrowRight
                  size={16}
                  color={theme.primary}
                  style={pressed && { opacity: 0.6 }}
                />
              </>
            )}
          </Pressable>
        </Link>
      </div>
      {loading ? (
        <Text style={[styles.statusText, { color: theme.mutedForeground }]}>
          {t('articles.loading')}
        </Text>
      ) : error ? (
        <Text style={[styles.statusText, { color: theme.destructive }]}>
          {t('articles.loadError', { error: error.message })}
        </Text>
      ) : (
        <div className={css.carouselWrap}>
          <div ref={scrollRef} className={css.scroller}>
            {articles.slice(0, HOME_ARTICLES_LIMIT).map((article) => (
              <ArticleCard key={article._id} article={article} locale={locale} />
            ))}
          </div>
          <div className={css.arrows}>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={t('a11y.previous')}
              onPress={() => scrollBy(-1)}
              style={({ pressed }) => [
                styles.navBtn,
                styles.navBtnLeft,
                { backgroundColor: theme.card, borderColor: theme.border },
                pressed && { opacity: 0.8 },
              ]}>
              <ChevronLeft size={20} color={theme.text} />
            </Pressable>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={t('a11y.next')}
              onPress={() => scrollBy(1)}
              style={({ pressed }) => [
                styles.navBtn,
                styles.navBtnRight,
                { backgroundColor: theme.card, borderColor: theme.border },
                pressed && { opacity: 0.8 },
              ]}>
              <ChevronRight size={20} color={theme.text} />
            </Pressable>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = StyleSheet.create({
  seeAll: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  seeAllText: {
    fontSize: 13,
    fontWeight: '600',
  },
  statusText: {
    fontSize: 14,
    paddingHorizontal: 16,
  },
  navBtn: {
    position: 'absolute',
    top: '50%',
    marginTop: -18,
    width: 36,
    height: 36,
    borderRadius: 999,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  navBtnLeft: {
    left: -12,
  },
  navBtnRight: {
    right: -12,
  },
});
